import React, { useState } from 'react';
import { Lock, RotateCcw, ArrowRight, Edit2, Trash2, Beaker, Upload, ShieldCheck } from 'lucide-react';
import { Slideout, Button, Pill } from '../';
import SkillReviewSlideout from './SkillReviewSlideout';
import PolicyDocumentsPanel from './PolicyDocumentsPanel';
import * as api from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';

/**
 * SkillPromotionCard — the ONE shared per-skill promotion card, identical across
 * EVERY agentlet type (Let / ExteLet / AutoLet / SysLet) and every phase
 * (Build / Decide / Act). Recreates the V1 card (verified in Chrome 2026-06-16,
 * docs/design/SKILL_CREATION_AND_PROMOTION_RULES.md) in V2 tokens with a 2×2
 * metadata grid; all interactions open V2 slideouts (no modals). It drives the
 * SHARED backend — none of these endpoints branch on archetype:
 *   advanceSkill · regressSkill · reviewSkill (via SkillReviewSlideout) ·
 *   deleteSkill · pushSkill.
 *
 * The card adapts its action row to `skill.skillPhase`. Each surface decides
 * which skills to pass (Build shows build, Decide shows decide, Act shows act),
 * but the CARD shape is identical everywhere.
 *
 * @param {string}   props.agentId
 * @param {Object}   props.skill        full skill row (skillType, skillPhase, evalScore, reviewStatus, autonomy, inferenceMode, riskLevel, pushed, regressionCount, guardrails)
 * @param {Function} props.onChanged    called after any mutation so the parent reloads
 * @param {boolean}  [props.canMutate]  false for runbook-managed AutoLets / PA-only SysLets — hides Configure/Delete (phase + review stay)
 * @param {Function} [props.onConfigure] () => void to open the configure surface (slideout / policy-config). Hidden when absent.
 * @param {Function} [props.renderEval]  (skill) => ReactNode rendered inside the Eval slideout. Hidden when absent.
 * @param {Function} [props.onEval]      (skill) => boolean. Optional direct-route for the "Eval" button.
 *                                       Return TRUE to indicate the caller handled it (e.g. opened the
 *                                       generate-config / review slideout directly) — the card then
 *                                       SKIPS its own intermediate Eval slideout, avoiding a zero-value
 *                                       extra click. Return falsy (or omit the prop) to fall back to the
 *                                       card's renderEval slideout. Keeps ExteLet + multi-action hubs intact.
 */
const TYPE_LABELS = {
  policy_skill: 'Policy', standard: 'Standard', workflow_skill: 'Workflow',
  connector_skill: 'Connector', wasm_skill: 'WASM',
};

export default function SkillPromotionCard({
  agentId, skill, onChanged, canMutate = true, onConfigure, renderEval, onEval,
  // Which lifecycle SURFACE this card is rendered on. Drives the action row so
  // each tab shows ONLY its phase's affordances (V1 parity): review controls
  // live on the Decide surface ONLY; Build shows Configure/eval/advance/delete or
  // a locked footer; Act shows the locked release footer.
  surface = 'build',
}) {
  const toastCtx = useToast();
  const addToast = (arg) => toastCtx?.addToast?.(arg);

  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'delete' | 'regress'
  const [reviewOpen, setReviewOpen] = useState(false);
  const [evalOpen, setEvalOpen] = useState(false);

  const ph = (skill.skillPhase || 'build').toLowerCase();
  const inBuild = ph === 'build';
  const inDecide = ph === 'decide';
  const inAct = ph === 'act';
  const rs = (skill.reviewStatus || 'pending_review').toLowerCase();
  const reviewed = rs === 'reviewed' || rs === 'promoted';
  const blocked = rs === 'blocked';
  const evalNum = typeof skill.evalScore === 'number'
    ? (skill.evalScore <= 1 ? Math.round(skill.evalScore * 100) : Math.round(skill.evalScore))
    : null;
  const canAdvanceToDecide = evalNum != null && evalNum >= 70;

  const run = async (fn, okTitle) => {
    setBusy(true);
    try {
      const res = await fn();
      const data = res?.data || res || {};
      if (res?.ok === false || data.advanced === false) {
        addToast({ tone: 'warning', title: `${skill.name} not updated`, description: data.message || res?.error || 'Backend refused the action.' });
      } else if (okTitle) {
        addToast({ tone: 'success', title: okTitle });
      }
      setConfirm(null);
      await onChanged?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Action failed', description: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  };

  const advance = () => run(() => api.advanceSkill(agentId, skill.id), `${skill.name} advanced`);
  const regress = () => run(() => api.regressSkill(agentId, skill.id), `${skill.name} regressed to Build`);
  const remove = () => run(() => api.deleteSkill(agentId, skill.id), 'Skill deleted');
  const rePush = () => run(() => api.pushSkill(agentId, skill.id), `${skill.name} re-pushed`);

  const autonomyLabel = (a) => {
    switch ((a || '').toUpperCase()) {
      case 'FULL': case 'SELF_AUTONOMOUS': return 'Autonomous';
      case 'HUMAN_CONTROLLED': return 'Human controlled';
      case 'SUPERVISED': return 'Supervised';
      case 'RESTRICTED': return 'Restricted';
      case 'PROBATION': case 'BLOCKED': return 'Blocked';
      default: return a ? a.replace(/_/g, ' ') : '—';
    }
  };
  const reviewLabel = blocked ? 'Blocked'
    : rs === 'promoted' ? 'autonomous'
    : reviewed ? (skill.reviewStatus || 'reviewed')
    : 'Pending';

  // Center-aligned content so the 2×2 grid reads uniformly across every card
  // in a row (left-aligned varying-width values looked "jumpy"). Fixed min-height
  // keeps the four boxes the same size regardless of value length.
  const Stat = ({ label, children }) => (
    <div className="rounded-v2-sm bg-v2-muted px-2.5 py-1.5 min-w-0 text-center flex flex-col justify-center min-h-[42px]">
      <div className="text-[9.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">{label}</div>
      <div className="text-[12px] font-semibold text-v2-ink mt-0.5 truncate">{children}</div>
    </div>
  );

  return (
    <div className="relative overflow-hidden border border-v2-border-subtle rounded-v2-md bg-v2-surface p-3.5 flex flex-col h-full">
      {/* Accent bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: 'linear-gradient(90deg, var(--v2-trust) 0%, transparent 100%)' }} />

      {/* Header — RC-131 layout.
       *
       * Pre-RC-131 the skill name and ALL header pills (skillType, phase,
       * eval %, regressionCount, EDITED—NEEDS RE-PUSH) lived in a single
       * `flex flex-wrap` container. Variable pill counts AND variable
       * name lengths produced variable header heights across cards in
       * the same row, which misaligned descriptions and the 2×2 metadata
       * grid below them. Operators reported this as a visual jaggedness
       * across the Skills grid — a card with a long name + the EDITED
       * pill could be 40-50px taller than its neighbours.
       *
       * Fix: split into two predictable rows, each with a min-height
       * that reserves space for the worst-case content. Every card now
       * has the same header height regardless of pill count or name
       * length (up to the reserved cap), so descriptions and the 2×2
       * grid land at the same Y position across the row.
       *
       *   Row 1 — skill name (full-width, can wrap) + the right-anchored
       *           risk-level pill. min-h reserves room for a two-line name.
       *   Row 2 — metadata pill strip (skillType, phase, eval%,
       *           regressionCount, EDITED). min-h reserves room for two
       *           lines of pills so cards with only the canonical pair
       *           still leave space for the "second row" that other cards
       *           in the same row may carry.
       */}
      <div className="mb-2">
        {/* Row 1 — name + right-anchored risk pill. flex-1+min-w-0 on the
            name lets it shrink and wrap inside the flex row without
            pushing the risk pill out of the card. */}
        <div className="flex items-start justify-between gap-2 min-h-[42px]">
          <span className="font-semibold text-[13.5px] text-v2-ink break-words flex-1 min-w-0">{skill.name}</span>
          {skill.riskLevel && (
            <Pill tone={skill.riskLevel === 'critical' || skill.riskLevel === 'high' ? 'danger'
              : skill.riskLevel === 'medium' ? 'warning' : 'muted'} size="sm">{skill.riskLevel}</Pill>
          )}
        </div>
        {/* Row 2 — metadata pill strip. min-h reserves two pill-rows of
            space; pills inside still wrap freely, so cards that DO
            carry many pills naturally grow to fit. */}
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5 min-h-[50px] content-start">
          {skill.skillType && <Pill tone="trust" size="sm">{TYPE_LABELS[skill.skillType] || skill.skillType}</Pill>}
          <Pill tone={inAct ? 'success' : inDecide ? 'cyan' : 'muted'} size="sm">{ph.toUpperCase()}</Pill>
          {evalNum != null && (
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-v2-sm ${evalNum >= 70 ? 'text-v2-success bg-v2-success-soft' : 'text-v2-danger bg-v2-danger-soft'}`}>
              {evalNum}%
            </span>
          )}
          {(skill.regressionCount || 0) > 0 && <Pill tone="warning" size="sm">regressed {skill.regressionCount}x</Pill>}
          {inAct && skill.pushed === false && <Pill tone="warning" size="sm">EDITED — NEEDS RE-PUSH</Pill>}
        </div>
      </div>
      {/* RC-131 v2 — wrap the description in a flex-1 spacer.
       *
       * Pre-v2 fix used `mt-auto` on the 2×2 grid to push it to the
       * bottom of the card, but `mt-auto` only consumes empty space
       * BEFORE its own element — anything below the 2×2 (PolicyDocuments
       * panel, Action row, Locked/Regress footer) anchors at the bottom
       * and the 2×2 ends up immediately above them rather than at a
       * consistent Y across cards. Result: cards with short descriptions
       * showed their 2×2 floating mid-card; cards with long descriptions
       * showed their 2×2 at the bottom.
       *
       * Correct fix: make the DESCRIPTION's wrapper the flex-grow
       * element. It eats the empty space below the text, pushing the
       * 2×2 + footer block down. Cards in the same row are equal-height
       * via the parent grid's stretch + this card's h-full, so the
       * 2×2 grids land at the same Y across every card in a row —
       * exactly what the operator asked for ("bottom align the content
       * starting with the Autonomy / Inference / Risk / Review
       * rectangles"). */}
      <div className="flex-1 min-h-0 mb-2.5">
        {skill.description && (
          <p className="text-[11.5px] text-v2-ink-muted m-0 leading-snug break-words">{skill.description}</p>
        )}
      </div>

      {/* 2×2 metadata grid — now naturally bottom-aligned by virtue of the
          flex-1 description wrapper above (which consumes empty space).
          Cards in a row have equal heights via grid stretch + h-full,
          so this grid lands at the same Y across the whole row. */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Stat label="Autonomy">{autonomyLabel(skill.autonomy)}</Stat>
        <Stat label="Inference">{skill.inferenceMode ? skill.inferenceMode.replace(/_/g, ' ').toLowerCase() : '—'}</Stat>
        <Stat label="Risk">{skill.riskLevel || 'low'}</Stat>
        <Stat label="Review">{reviewLabel}</Stat>
      </div>

      {/* Policy Documents (advisory RAG corpus) — restored V1 surface, policy skills only.
          Upload is enabled in Build (editable); locked phases show the list read-only. */}
      {skill.skillType === 'policy_skill' && (
        <PolicyDocumentsPanel skillId={skill.id} canEdit={canMutate && inBuild} onChanged={onChanged} />
      )}

      {/* Action row (phase-locked) */}
      {confirm ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11.5px] text-v2-ink-muted">
            {confirm === 'delete' ? 'Delete this skill + its eval?'
              : 'Regress to Build? Clears its eval + review and unlocks editing.'}
          </span>
          <Button variant="danger" size="sm" loading={busy}
            onClick={() => (confirm === 'delete' ? remove() : regress())}>
            {confirm === 'delete' ? 'Delete' : 'Regress'}
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirm(null)}>Cancel</Button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* ── BUILD surface — V1 SkillCardBuild. Build skills: Configure/Eval/
              Advance(→Decide)/Delete. Locked (decide/act) skills: Locked pill +
              Regress + Advance. NO review here (review lives on Decide). ── */}
          {surface === 'build' && (inBuild ? (
            <>
              {onConfigure && canMutate && (
                <Button variant="ghost" size="sm" iconLeft={<Edit2 size={12} />} onClick={onConfigure}>Configure</Button>
              )}
              {renderEval && (
                <Button variant="ghost" size="sm" iconLeft={<Beaker size={12} />} onClick={() => { if (!(onEval && onEval(skill))) setEvalOpen(true); }}>Eval</Button>
              )}
              <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={12} />}
                disabled={busy || !canAdvanceToDecide} loading={busy} onClick={advance}
                title={canAdvanceToDecide ? 'Promote to Decide' : 'Needs a completed eval with a passing score (≥ 70%) first'}>
                Advance
              </Button>
              {canMutate && (
                <Button variant="ghost" size="sm" className="text-v2-danger" iconLeft={<Trash2 size={12} />}
                  disabled={busy} onClick={() => setConfirm('delete')}>Delete</Button>
              )}
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-v2-sm"
                style={{ background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
                <Lock size={10} /> Locked ({ph.toUpperCase()})
              </span>
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={12} />} disabled={busy}
                onClick={() => setConfirm('regress')}>Regress to Build</Button>
              {!inAct && (
                <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={12} />} disabled={busy} loading={busy}
                  onClick={advance} title="Advance to the next phase">Advance</Button>
              )}
            </>
          ))}

          {/* ── DECIDE surface — V1 Skill Review Matrix. Review (slideout) + Advance
              to Act. Act-phase skills read locked/Active. ── */}
          {surface === 'decide' && (inAct ? (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-v2-sm"
                style={{ background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
                <Lock size={10} /> Locked (ACT)
              </span>
              {reviewed && <Pill tone="success" size="sm">Active</Pill>}
              {/* An edited-in-Act skill (pushed === false) shows the "EDITED — NEEDS RE-PUSH"
                  pill in the header. Re-push re-materializes the edited policy into enforcement
                  WITHOUT leaving Act — so it must be offered wherever that pill can appear, not
                  only on the Act tab. Omitting it here left the Decide surface telling operators
                  to re-push with no control to do it, forcing a destructive Regress. */}
              {skill.pushed === false && canMutate && (
                <Button variant="secondary" size="sm" iconLeft={<Upload size={12} />} loading={busy} onClick={rePush}>
                  Re-push
                </Button>
              )}
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={12} />} disabled={busy}
                onClick={() => setConfirm('regress')}>Regress to Build</Button>
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-v2-sm"
                style={{ background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
                <Lock size={10} /> Locked (DECIDE)
              </span>
              {renderEval && (
                <Button variant="ghost" size="sm" iconLeft={<Beaker size={12} />} onClick={() => { if (!(onEval && onEval(skill))) setEvalOpen(true); }}>Eval</Button>
              )}
              <Button variant="primary" size="sm" iconLeft={<ShieldCheck size={12} />} disabled={busy}
                onClick={() => setReviewOpen(true)}>
                {reviewed || blocked ? 'Update Review' : 'Start Review'}
              </Button>
              {reviewed && !blocked && (
                <Button variant="secondary" size="sm" iconLeft={<ArrowRight size={12} />} loading={busy}
                  onClick={advance} title="Promote this reviewed skill to Act">Advance to Act</Button>
              )}
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={12} />} disabled={busy}
                onClick={() => setConfirm('regress')}>Regress to Build</Button>
            </>
          ))}

          {/* ── ACT surface — locked release footer + Re-push. ── */}
          {surface === 'act' && (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-v2-sm"
                style={{ background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
                <Lock size={10} /> Locked (ACT)
              </span>
              {skill.pushed === false && canMutate && (
                <Button variant="secondary" size="sm" iconLeft={<Upload size={12} />} loading={busy} onClick={rePush}>
                  Re-push
                </Button>
              )}
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={12} />} disabled={busy}
                onClick={() => setConfirm('regress')}>Regress to Build</Button>
            </>
          )}
        </div>
      )}

      {/* Review slideout (V2, no modal) */}
      <SkillReviewSlideout
        isOpen={reviewOpen}
        onClose={() => setReviewOpen(false)}
        agentId={agentId}
        skill={skill}
        onReviewed={onChanged}
      />

      {/* Eval slideout — body injected per surface (policy eval, native eval, …) */}
      {renderEval && (
        <Slideout
          isOpen={evalOpen}
          onClose={() => setEvalOpen(false)}
          ariaLabel="Skill eval"
          width={560}
          header={
            <div>
              <div className="text-[13.5px] font-bold text-v2-ink">Eval</div>
              <div className="text-[11.5px] text-v2-ink-muted truncate">{skill.name}</div>
            </div>
          }
        >
          {renderEval(skill)}
        </Slideout>
      )}
    </div>
  );
}
